import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import api from '@/services/api'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const email = searchParams.get('email') || ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const resetMutation = useMutation({
    mutationFn: (data: { token: string; email: string; password: string }) =>
      api.auth.resetPassword(data),
    onSuccess: () => setSuccess(true),
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to reset password. The link may have expired.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    resetMutation.mutate({ token, email, password })
  }

  if (!token || !email) {
    return (
      <div className="container" style={{ maxWidth: '450px', margin: '4rem auto', padding: '2rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Invalid Reset Link</h1>
          <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem' }}>
            This password reset link is invalid or incomplete. Please request a new one.
          </p>
          <button onClick={() => navigate('/auth/forgot-password')} style={{ width: '100%' }}>
            Request New Link
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="container" style={{ maxWidth: '450px', margin: '4rem auto', padding: '2rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#98ca3f' }}>&#10003;</div>
          <h1 style={{ marginBottom: '0.75rem' }}>Password Reset</h1>
          <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem' }}>
            Your password has been reset successfully. You can now sign in with your new password.
          </p>
          <button onClick={() => navigate('/auth')} style={{ width: '100%' }}>
            Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container" style={{ maxWidth: '450px', margin: '4rem auto', padding: '2rem' }}>
      <div className="card">
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          Set new password
        </h1>
        <p style={{ textAlign: 'center', color: 'var(--color-gray-600)', marginBottom: '2rem' }}>
          Enter your new password below.
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={resetMutation.isPending}
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
